import { createClient } from "@supabase/supabase-js";

const url = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const publishableKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string | undefined;

const hashType = new URLSearchParams(window.location.hash.slice(1)).get("type");
const queryType = new URLSearchParams(window.location.search).get("type");
export const authLandingType = hashType ?? queryType;

if (!url || !publishableKey) {
  throw new Error("Supabase staging environment variables are not configured");
}

const requestTimeoutMs = 20_000;

const boundedFetch: typeof fetch = async (input, init = {}) => {
  const controller = new AbortController();
  const upstreamSignal = init.signal;
  const abortFromUpstream = () => controller.abort(upstreamSignal?.reason);
  if (upstreamSignal) {
    if (upstreamSignal.aborted) abortFromUpstream();
    else upstreamSignal.addEventListener("abort", abortFromUpstream, { once: true });
  }
  const timer = window.setTimeout(
    () => controller.abort(new DOMException("Request timed out", "TimeoutError")),
    requestTimeoutMs,
  );
  try {
    return await fetch(input, { ...init, signal: controller.signal });
  } finally {
    window.clearTimeout(timer);
    upstreamSignal?.removeEventListener("abort", abortFromUpstream);
  }
};

export const supabase = createClient(url, publishableKey, {
  db: { schema: "api" },
  global: { fetch: boundedFetch },
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
  },
});

export async function rpc<T>(name: string, args: Record<string, unknown> = {}): Promise<T> {
  try {
    const { data, error } = await supabase.rpc(name, args);
    if (error) throw error;
    return data as T;
  } catch (error) {
    if (error instanceof DOMException && ["AbortError", "TimeoutError"].includes(error.name)) {
      throw new Error("The staging service did not respond within 20 seconds. Please try again.");
    }
    throw error;
  }
}

export function requestId(prefix: string): string {
  return `${prefix}:${crypto.randomUUID()}`;
}
